/**
 * Which days near the customer's ZIP are cheaper, for the date step — and, on
 * the way, whether we clean there at all, for step 1.
 *
 * One request serves both steps. Held per ZIP for a few minutes so going back
 * and forth between the steps does not ask again every time, but not for long:
 * the answer is other people's bookings, and those move.
 */

import momentTz from "moment-timezone";
import { useEffect, useState } from "react";
import { AreaCalendar, fetchAreaCalendar, isZip } from "../lib/proximity";

const FRESH_MS = 5 * 60_000;
const cache = new Map<string, { at: number; answer: Promise<AreaCalendar> }>();
/** The answers that have arrived, for a synchronous look (`peekAreaCalendar`). */
const settled = new Map<string, AreaCalendar>();

function calendarFor(zip: string): Promise<AreaCalendar> {
  const held = cache.get(zip);
  if (held && Date.now() - held.at < FRESH_MS) return held.answer;
  // Today in New York: the endpoint clips anything earlier, and the business
  // runs on that calendar, not the customer's.
  const from = momentTz.tz("America/New_York").format("YYYY-MM-DD");
  const answer = fetchAreaCalendar(zip, from);
  cache.set(zip, { at: Date.now(), answer });
  // An unreachable answer is not kept, so the next look asks again.
  answer.then(
    (result) => {
      if (result.status === "unreachable") cache.delete(zip);
      else settled.set(zip, result);
    },
    () => cache.delete(zip)
  );
  return answer;
}

/** The last calendar that arrived for a ZIP, if any; never asks. */
export function peekAreaCalendar(zip: string): AreaCalendar | null {
  return settled.get(zip) ?? null;
}

/**
 * Forget a ZIP's calendar, so the date step asks again. Used when checkout
 * learns the area price moved: showing the old saving on step 2 after the
 * customer was told it is gone would contradict them five seconds later.
 */
export function forgetAreaCalendar(zip: string): void {
  cache.delete(zip);
  settled.delete(zip);
}

export function useAreaCalendar(zip: string): { calendar: AreaCalendar | null } {
  const complete = isZip(zip);
  const [state, setState] = useState<{ zip: string; calendar: AreaCalendar | null }>({
    zip: "",
    calendar: null,
  });

  useEffect(() => {
    if (!complete) return;
    let cancelled = false;
    calendarFor(zip).then(
      (calendar) => {
        if (!cancelled) setState({ zip, calendar });
      },
      () => {
        if (!cancelled) setState({ zip, calendar: { status: "unreachable" } });
      }
    );
    return () => {
      cancelled = true;
    };
  }, [zip, complete]);

  return { calendar: complete && state.zip === zip ? state.calendar : null };
}
